import React, {
  useRef,
  useState,
  useCallback,
  useMemo,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Animated,
  Keyboard,
  KeyboardTypeOptions,
} from 'react-native';
import { createStyles, getColors, getSizes } from './DbInput.styles';
import type {
  DbInputProps,
  DbInputRef,
  InputIconConfig,
  InputRule,
  XSSFilterOptions,
  InputType,
} from './DbInput.types';
import DbIcon from '../DbIcon';

// ==================== XSS 防护工具函数 ====================

/** HTML 实体编码映射 */
const HTML_ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;',
};

/** 编码 HTML 特殊字符 */
const encodeHtmlEntities = (str: string): string => {
  return str.replace(/[&<>"'`=/]/g, (char) => HTML_ENTITIES[char] || char);
};

/** 移除 HTML 标签 */
const stripHtmlTags = (str: string): string => {
  return str.replace(/<[^>]*>/g, '');
};

/** 移除 script 标签及其内容 */
const stripScriptTags = (str: string): string => {
  return str.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
};

/** 移除事件处理器属性 */
const stripEventHandlers = (str: string): string => {
  return str.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
};

/** 移除 javascript: 协议 */
const stripJavascriptProtocol = (str: string): string => {
  return str.replace(/javascript\s*:/gi, '');
};

/** 移除 data: 协议（可能包含恶意内容） */
const stripDataProtocol = (str: string): string => {
  return str.replace(/data\s*:[^,]*,/gi, '');
};

/** 综合 XSS 过滤 */
const sanitizeXSS = (
  value: string,
  options: XSSFilterOptions = {}
): string => {
  const {
    enabled = true,
    stripHtml = true,
    stripScript = true,
    encodeEntities = false,
    customFilter,
  } = options;

  if (!enabled) return value;

  let sanitized = value;

  // 1. 移除 script 标签
  if (stripScript) {
    sanitized = stripScriptTags(sanitized);
  }

  // 2. 移除事件处理器
  sanitized = stripEventHandlers(sanitized);

  // 3. 移除危险协议
  sanitized = stripJavascriptProtocol(sanitized);
  sanitized = stripDataProtocol(sanitized);

  // 4. 移除 HTML 标签
  if (stripHtml) {
    sanitized = stripHtmlTags(sanitized);
  }

  // 5. 编码特殊字符
  if (encodeEntities) {
    sanitized = encodeHtmlEntities(sanitized);
  }

  // 6. 自定义过滤
  if (customFilter) {
    sanitized = customFilter(sanitized);
  }

  return sanitized;
};

// ==================== 辅助函数 ====================

/** 根据输入类型获取键盘类型 */
const getKeyboardType = (type: InputType): KeyboardTypeOptions => {
  switch (type) {
    case 'number':
      return 'decimal-pad';
    case 'integer':
      return 'number-pad';
    case 'phone':
      return 'phone-pad';
    case 'email':
      return 'email-address';
    case 'url':
      return 'url';
    default:
      return 'default';
  }
};

/** 格式化电话号码 (xxx xxxx xxxx) */
const formatPhoneNumber = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{0,3})(\d{0,4})(\d{0,4})$/);
  if (match) {
    return [match[1], match[2], match[3]].filter(Boolean).join(' ');
  }
  return value;
};

/** 验证数字输入 */
const validateNumberInput = (
  value: string,
  precision?: number,
  allowNegative: boolean = false
): string => {
  // 允许空值
  if (!value) return '';

  // 构建正则
  let pattern: RegExp;
  if (precision === 0) {
    pattern = allowNegative ? /^-?\d*$/ : /^\d*$/;
  } else if (precision && precision > 0) {
    pattern = allowNegative
      ? new RegExp(`^-?\\d*\\.?\\d{0,${precision}}$`)
      : new RegExp(`^\\d*\\.?\\d{0,${precision}}$`);
  } else {
    pattern = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
  }

  // 如果匹配则返回，否则返回空
  if (pattern.test(value)) {
    return value;
  }
  return '';
};

// ==================== 主组件 ====================

const DbInput = forwardRef<DbInputRef, DbInputProps>((props, ref) => {
  const {
    type = 'text',
    value,
    defaultValue = '',
    placeholder,
    placeholderTextColor,
    // 标签
    label,
    labelPosition = 'top',
    required = false,
    hint,
    error: errorProp,
    status = 'default',
    // 图标
    leftIcon,
    rightIcon,
    onLeftIconPress,
    onRightIconPress,
    // 密码
    showPasswordToggle = false,
    passwordVisibleIcon,
    passwordHiddenIcon,
    // 清除
    showClear = false,
    clearIcon,
    onClear,
    // 字数统计
    showCount = false,
    maxLength,
    // 数字
    precision,
    min,
    max,
    showStepper = false,
    step = 1,
    // 电话
    formatPhone = false,
    phonePrefix = '+86',
    // textarea
    rows = 4,
    autoGrow = false,
    minHeight,
    maxHeight,
    // 校验
    rules,
    validateTrigger = 'onBlur',
    onValidate,
    // XSS
    xssFilter = true,
    // 样式
    disabled = false,
    readonly = false,
    shape = 'default',
    size = 'medium',
    style,
    inputContainerStyle,
    inputStyle,
    labelStyle,
    hintStyle,
    errorStyle,
    // 回调
    onChangeText,
    onSubmit,
    onFocus,
    onBlur,
    // 其他
    ...restProps
  } = props;

  const styles = useMemo(() => createStyles('light', size), [size]);
  const colors = useMemo(() => getColors('light'), []);
  const sizeConfig = useMemo(() => getSizes(size), [size]);

  const inputRef = useRef<TextInput>(null);
  const [inputValue, setInputValue] = useState(defaultValue);
  const [isFocused, setIsFocused] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [textareaHeight, setTextareaHeight] = useState<number | undefined>(undefined);

  // 浮动标签动画
  const floatingAnim = useRef(new Animated.Value(defaultValue ? 1 : 0)).current;

  // 受控/非受控
  const currentValue = value !== undefined ? value : inputValue;
  const hasValue = currentValue.length > 0;
  const displayError = errorProp || validationError;

  // XSS 过滤选项
  const xssOptions: XSSFilterOptions = useMemo(() => {
    if (typeof xssFilter === 'boolean') {
      return { enabled: xssFilter };
    }
    return xssFilter;
  }, [xssFilter]);

  // 更新浮动标签动画
  useEffect(() => {
    if (labelPosition === 'floating') {
      Animated.timing(floatingAnim, {
        toValue: isFocused || hasValue ? 1 : 0,
        duration: 150,
        useNativeDriver: false,
      }).start();
    }
  }, [isFocused, hasValue, labelPosition, floatingAnim]);

  // ==================== 校验逻辑 ====================

  const validate = useCallback(async (): Promise<boolean> => {
    if (!rules || rules.length === 0) return true;

    for (const rule of rules) {
      // 必填校验
      if (rule.required && !currentValue.trim()) {
        const error = rule.requiredMessage || '此字段为必填项';
        setValidationError(error);
        onValidate?.(false, error);
        return false;
      }

      // 最小长度
      if (rule.minLength && currentValue.length < rule.minLength) {
        const error = rule.minLengthMessage || `最少输入 ${rule.minLength} 个字符`;
        setValidationError(error);
        onValidate?.(false, error);
        return false;
      }

      // 最大长度
      if (rule.maxLength && currentValue.length > rule.maxLength) {
        const error = rule.maxLengthMessage || `最多输入 ${rule.maxLength} 个字符`;
        setValidationError(error);
        onValidate?.(false, error);
        return false;
      }

      // 正则校验
      if (rule.pattern && !rule.pattern.test(currentValue)) {
        const error = rule.patternMessage || '格式不正确';
        setValidationError(error);
        onValidate?.(false, error);
        return false;
      }

      // 自定义校验
      if (rule.validator) {
        const result = await rule.validator(currentValue);
        if (result !== true) {
          const error = typeof result === 'string' ? result : '校验失败';
          setValidationError(error);
          onValidate?.(false, error);
          return false;
        }
      }
    }

    setValidationError(null);
    onValidate?.(true);
    return true;
  }, [rules, currentValue, onValidate]);

  // ==================== 事件处理 ====================

  const handleChangeText = useCallback(
    (text: string) => {
      let processedText = text;
      const rawText = text;

      // 根据类型处理输入
      switch (type) {
        case 'number':
        case 'integer':
          const validNumber = validateNumberInput(
            text,
            type === 'integer' ? 0 : precision
          );
          if (validNumber === '' && text !== '') {
            return; // 无效输入，忽略
          }
          processedText = validNumber;

          // 范围限制
          if (processedText && !processedText.endsWith('.')) {
            const num = parseFloat(processedText);
            if (min !== undefined && num < min) {
              processedText = String(min);
            }
            if (max !== undefined && num > max) {
              processedText = String(max);
            }
          }
          break;

        case 'phone':
          // 只保留数字
          processedText = text.replace(/\D/g, '').slice(0, 11);
          break;
      }

      // XSS 过滤
      const sanitizedText = sanitizeXSS(processedText, xssOptions);

      // 更新值
      if (value === undefined) {
        setInputValue(sanitizedText);
      }
      onChangeText?.(sanitizedText, rawText);

      // 触发校验
      if (validateTrigger === 'onChange') {
        validate();
      }
    },
    [type, precision, min, max, xssOptions, value, onChangeText, validateTrigger, validate]
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    onFocus?.();
  }, [onFocus]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    onBlur?.();

    if (validateTrigger === 'onBlur') {
      validate();
    }
  }, [onBlur, validateTrigger, validate]);

  const handleSubmit = useCallback(() => {
    Keyboard.dismiss();
    if (validateTrigger === 'onSubmit') {
      validate().then((isValid) => {
        if (isValid) {
          onSubmit?.(currentValue);
        }
      });
    } else {
      onSubmit?.(currentValue);
    }
  }, [currentValue, onSubmit, validateTrigger, validate]);

  const handleClear = useCallback(() => {
    if (value === undefined) {
      setInputValue('');
    }
    onChangeText?.('', '');
    onClear?.();
    inputRef.current?.focus();
    setValidationError(null);
  }, [value, onChangeText, onClear]);

  const togglePasswordVisibility = useCallback(() => {
    setPasswordVisible((prev) => !prev);
  }, []);

  const handleStepperPress = useCallback(
    (direction: 'up' | 'down') => {
      const num = parseFloat(currentValue) || 0;
      const newValue = direction === 'up' ? num + step : num - step;

      // 范围限制
      let finalValue = newValue;
      if (min !== undefined && finalValue < min) finalValue = min;
      if (max !== undefined && finalValue > max) finalValue = max;

      // 精度处理
      const formattedValue =
        precision !== undefined ? finalValue.toFixed(precision) : String(finalValue);

      if (value === undefined) {
        setInputValue(formattedValue);
      }
      onChangeText?.(formattedValue, formattedValue);
    },
    [currentValue, step, min, max, precision, value, onChangeText]
  );

  // ==================== 暴露方法 ====================

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    blur: () => inputRef.current?.blur(),
    clear: handleClear,
    getValue: () => currentValue,
    setValue: (val: string) => {
      const sanitized = sanitizeXSS(val, xssOptions);
      if (value === undefined) {
        setInputValue(sanitized);
      }
      onChangeText?.(sanitized, val);
    },
    validate,
    resetValidation: () => setValidationError(null),
  }));

  // ==================== 渲染图标 ====================

  const renderIcon = (
    iconConfig: InputIconConfig | React.ReactNode,
    position: 'left' | 'right',
    onPress?: () => void
  ) => {
    const containerStyle = position === 'left' ? styles.leftIcon : styles.rightIcon;

    // 如果是 ReactNode
    if (React.isValidElement(iconConfig)) {
      if (onPress) {
        return (
          <TouchableOpacity style={[containerStyle, styles.iconTouchable]} onPress={onPress}>
            {iconConfig}
          </TouchableOpacity>
        );
      }
      return <View style={containerStyle}>{iconConfig}</View>;
    }

    // 如果是 IconConfig
    const config = iconConfig as InputIconConfig;
    const icon = (
      <DbIcon
        name={config.name}
        family={config.family || 'Ionicons'}
        size={config.size || sizeConfig.iconSize}
        color={config.color || colors.icon}
      />
    );

    if (onPress) {
      return (
        <TouchableOpacity style={[containerStyle, styles.iconTouchable]} onPress={onPress}>
          {icon}
        </TouchableOpacity>
      );
    }

    return <View style={containerStyle}>{icon}</View>;
  };

  // ==================== 渲染清除按钮 ====================

  const renderClearButton = () => {
    if (!showClear || !hasValue || disabled || readonly) return null;

    const iconConfig = clearIcon || { name: 'close-circle', family: 'Ionicons' as const };

    return (
      <TouchableOpacity style={styles.clearButton} onPress={handleClear}>
        <DbIcon
          name={typeof iconConfig === 'object' ? iconConfig.name : 'close-circle'}
          family={(iconConfig as InputIconConfig).family || 'Ionicons'}
          size={(iconConfig as InputIconConfig).size || 16}
          color={(iconConfig as InputIconConfig).color || colors.clear}
        />
      </TouchableOpacity>
    );
  };

  // ==================== 渲染密码切换 ====================

  const renderPasswordToggle = () => {
    if (type !== 'password' || !showPasswordToggle) return null;

    const visibleIcon = passwordVisibleIcon || { name: 'eye-outline', family: 'Ionicons' as const };
    const hiddenIcon = passwordHiddenIcon || { name: 'eye-off-outline', family: 'Ionicons' as const };
    const currentIcon = passwordVisible ? visibleIcon : hiddenIcon;

    return (
      <TouchableOpacity style={styles.passwordToggle} onPress={togglePasswordVisibility}>
        <DbIcon
          name={currentIcon.name}
          family={currentIcon.family || 'Ionicons'}
          size={currentIcon.size || sizeConfig.iconSize}
          color={currentIcon.color || colors.icon}
        />
      </TouchableOpacity>
    );
  };

  // ==================== 渲染步进器 ====================

  const renderStepper = () => {
    if (!showStepper || (type !== 'number' && type !== 'integer')) return null;

    const num = parseFloat(currentValue) || 0;
    const canDecrease = min === undefined || num > min;
    const canIncrease = max === undefined || num < max;

    return (
      <View style={styles.stepperContainer}>
        <TouchableOpacity
          style={[styles.stepperButton, !canDecrease && styles.stepperButtonDisabled]}
          onPress={() => handleStepperPress('down')}
          disabled={!canDecrease || disabled}
        >
          <DbIcon name="remove" family="Ionicons" size={16} color={colors.text} />
        </TouchableOpacity>
        <View style={styles.stepperDivider} />
        <TouchableOpacity
          style={[styles.stepperButton, !canIncrease && styles.stepperButtonDisabled]}
          onPress={() => handleStepperPress('up')}
          disabled={!canIncrease || disabled}
        >
          <DbIcon name="add" family="Ionicons" size={16} color={colors.text} />
        </TouchableOpacity>
      </View>
    );
  };

  // ==================== 渲染标签 ====================

  const renderLabel = () => {
    if (!label || labelPosition === 'floating') return null;

    return (
      <View
        style={[
          styles.labelContainer,
          labelPosition === 'left' && styles.labelContainerLeft,
        ]}
      >
        <Text
          style={[
            styles.label,
            labelPosition === 'left' && styles.labelLeft,
            labelStyle,
          ]}
        >
          {label}
        </Text>
        {required && <Text style={styles.required}>*</Text>}
      </View>
    );
  };

  // ==================== 渲染浮动标签 ====================

  const renderFloatingLabel = () => {
    if (!label || labelPosition !== 'floating') return null;

    const translateY = floatingAnim.interpolate({
      inputRange: [0, 1],
      outputRange: [0, -(sizeConfig.height / 2 + 2)],
    });

    const fontSize = floatingAnim.interpolate({
      inputRange: [0, 1],
      outputRange: [sizeConfig.fontSize, sizeConfig.labelFontSize - 2],
    });

    return (
      <Animated.View
        style={[
          styles.floatingLabelContainer,
          { transform: [{ translateY }] },
          (isFocused || hasValue) && styles.floatingLabelActive,
        ]}
        pointerEvents="none"
      >
        <Animated.Text
          style={[
            styles.floatingLabel,
            { fontSize },
            (isFocused || hasValue) && styles.floatingLabelActive2,
          ]}
        >
          {label}
          {required && <Text style={styles.required}> *</Text>}
        </Animated.Text>
      </Animated.View>
    );
  };

  // ==================== 渲染底部 ====================

  const renderFooter = () => {
    const showHint = hint && !displayError;
    const showError = !!displayError;
    const shouldShowCount = showCount && maxLength;

    if (!showHint && !showError && !shouldShowCount) return null;

    const currentLength = currentValue.length;
    const isNearLimit = maxLength && currentLength >= maxLength * 0.9;
    const isOverLimit = maxLength && currentLength > maxLength;

    return (
      <View style={styles.footer}>
        <View style={styles.footerLeft}>
          {showError && <Text style={[styles.error, errorStyle]}>{displayError}</Text>}
          {showHint && <Text style={[styles.hint, hintStyle]}>{hint}</Text>}
        </View>
        {shouldShowCount && (
          <Text
            style={[
              styles.count,
              isNearLimit ? styles.countWarning : undefined,
              isOverLimit ? styles.countError : undefined,
            ].filter(Boolean)}
          >
            {currentLength}/{maxLength}
          </Text>
        )}
      </View>
    );
  };

  // ==================== 渲染电话区号 ====================

  const renderPhonePrefix = () => {
    if (type !== 'phone' || !phonePrefix) return null;

    return (
      <>
        <Text style={styles.phonePrefix}>{phonePrefix}</Text>
        <View style={styles.phoneDivider} />
      </>
    );
  };

  // ==================== 计算容器样式 ====================

  const getInputContainerStyles = (): any[] => {
    const containerStyles: any[] = [styles.inputContainer];

    // 形状
    if (shape === 'round') {
      containerStyles.push(styles.inputContainerRound);
    } else if (shape === 'underline') {
      containerStyles.push(styles.inputContainerUnderline);
      if (isFocused) {
        containerStyles.push(styles.inputContainerUnderlineFocused);
      }
    }

    // 聚焦状态
    if (isFocused && shape !== 'underline') {
      containerStyles.push(styles.inputContainerFocused);
    }

    // 状态
    if (displayError || status === 'error') {
      containerStyles.push(styles.inputContainerError);
    } else if (status === 'success') {
      containerStyles.push(styles.inputContainerSuccess);
    } else if (status === 'warning') {
      containerStyles.push(styles.inputContainerWarning);
    }

    // 禁用/只读
    if (disabled) {
      containerStyles.push(styles.inputContainerDisabled);
    } else if (readonly) {
      containerStyles.push(styles.inputContainerReadonly);
    }

    return containerStyles;
  };

  // ==================== 计算输入框样式 ====================

  const getInputStyles = (): any[] => {
    const inputStyles: any[] = [styles.input];

    if (type === 'textarea') {
      inputStyles.push(styles.textarea);
      if (textareaHeight) {
        inputStyles.push({ height: textareaHeight } as any);
      } else if (rows) {
        inputStyles.push({ minHeight: rows * 20 + 20 } as any);
      }
    }

    if (disabled) {
      inputStyles.push(styles.inputDisabled);
    } else if (readonly) {
      inputStyles.push(styles.inputReadonly);
    }

    return inputStyles;
  };

  // ==================== 显示值处理 ====================

  const displayValue = useMemo(() => {
    if (type === 'phone' && formatPhone) {
      return formatPhoneNumber(currentValue);
    }
    return currentValue;
  }, [type, formatPhone, currentValue]);

  // ==================== 主渲染 ====================

  const renderInputContent = () => (
    <View style={styles.inputWrapper}>
      <View style={[getInputContainerStyles(), inputContainerStyle]}>
        {/* 电话区号 */}
        {renderPhonePrefix()}

        {/* 左侧图标 */}
        {leftIcon && renderIcon(leftIcon, 'left', onLeftIconPress)}

        {/* 浮动标签 */}
        {renderFloatingLabel()}

        {/* 输入框 */}
        <TextInput
          ref={inputRef}
          style={[getInputStyles(), inputStyle]}
          value={displayValue}
          placeholder={labelPosition === 'floating' && !isFocused && !hasValue ? undefined : placeholder}
          placeholderTextColor={placeholderTextColor || colors.placeholder}
          onChangeText={handleChangeText}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onSubmitEditing={handleSubmit}
          editable={!disabled && !readonly}
          secureTextEntry={type === 'password' && !passwordVisible}
          keyboardType={getKeyboardType(type)}
          multiline={type === 'textarea'}
          numberOfLines={type === 'textarea' ? rows : 1}
          maxLength={maxLength}
          returnKeyType={type === 'textarea' ? 'default' : 'done'}
          textAlignVertical={type === 'textarea' ? 'top' : 'center'}
          onContentSizeChange={
            type === 'textarea' && autoGrow
              ? (e) => {
                  const height = e.nativeEvent.contentSize.height;
                  const finalHeight = Math.min(
                    Math.max(height, minHeight || 80),
                    maxHeight || 200
                  );
                  setTextareaHeight(finalHeight);
                }
              : undefined
          }
          {...restProps}
        />

        {/* 清除按钮 */}
        {renderClearButton()}

        {/* 密码切换 */}
        {renderPasswordToggle()}

        {/* 右侧图标 */}
        {rightIcon && renderIcon(rightIcon, 'right', onRightIconPress)}

        {/* 步进器 */}
        {renderStepper()}
      </View>

      {/* 底部提示/错误/字数 */}
      {renderFooter()}
    </View>
  );

  // 左侧标签布局
  if (labelPosition === 'left') {
    return (
      <View style={[styles.container, styles.leftLabelContainer, style]}>
        {renderLabel()}
        {renderInputContent()}
      </View>
    );
  }

  // 顶部标签 / 浮动标签布局
  return (
    <View style={[styles.container, style]}>
      {renderLabel()}
      {renderInputContent()}
    </View>
  );
});

DbInput.displayName = 'DbInput';

export default DbInput;
export type {
  DbInputProps,
  DbInputRef,
  InputType,
  InputStatus,
  InputIconConfig,
  InputRule,
  XSSFilterOptions,
} from './DbInput.types';
